import React from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { SkillAudience, SkillScope, SkillShape, SkillType } from "@/model/interface/Prompt";
import { AVAILABLE_AI_TOOLS } from "@/model/interface/AiAgent";

export const SKILL_AUDIENCE_COPY: Record<SkillAudience, { label: string; blurb: string }> = {
  GLOBAL: {
    label: "Global skill",
    blurb: "Goes to every agent automatically. There is nothing to assign.",
  },
  AGENT: {
    label: "Agent skill",
    blurb: "Goes only to the templates and agents you add it to.",
  },
};

export const SKILL_TYPE_COPY: Record<SkillType, { label: string; blurb: string }> = {
  POLICY: {
    label: "Always on",
    blurb: "Conduct that applies to the whole call. It goes into every part of the conversation.",
  },
  PHASE: {
    label: "Call stage",
    blurb:
      "A stage of the call with its own steps and tools. On Vapi this becomes a dedicated specialist the agent hands off to; elsewhere it is simply added to the prompt.",
  },
};

export const SKILL_SCOPE_COPY: Record<SkillScope, { label: string; blurb: string }> = {
  ALL_MEMBERS: {
    label: "Any moment of the call",
    blurb:
      "Can come up at any point, including in the middle of another task: mishearing, a correction, an upset caller.",
  },
  ROUTER_ONLY: {
    label: "Before a stage starts",
    blurb:
      "Only relevant while the agent is working out what the call is about: the reason for calling, whether it's in scope, offering a callback. Kept out of call stages, which makes those stages faster to start.",
  },
};

export const EMPTY_SKILL_SHAPE: SkillShape = {
  skillType: "POLICY",
  skillScope: "ALL_MEMBERS",
  skillAudience: "AGENT",
  memberTools: [],
  handoffDescription: "",
  openingLine: "",
};

export default function SkillShapeFields({
  shape,
  onChange,
}: {
  shape: SkillShape;
  onChange: (next: Partial<SkillShape>) => void;
}) {
  const toggleTool = (toolName: string) => {
    onChange({
      memberTools: shape.memberTools.includes(toolName)
        ? shape.memberTools.filter((memberTool) => memberTool !== toolName)
        : [...shape.memberTools, toolName],
    });
  };

  return (
    <>
      <div className="space-y-1.5">
        <Label>Applies to</Label>
        <Select
          value={shape.skillAudience}
          onValueChange={(nextAudience) => onChange({ skillAudience: nextAudience as SkillAudience })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(SKILL_AUDIENCE_COPY) as SkillAudience[]).map((audience) => (
              <SelectItem key={audience} value={audience}>
                {SKILL_AUDIENCE_COPY[audience].label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">{SKILL_AUDIENCE_COPY[shape.skillAudience].blurb}</p>
      </div>

      <div className="space-y-1.5">
        <Label>Type</Label>
        <Select value={shape.skillType} onValueChange={(nextType) => onChange({ skillType: nextType as SkillType })}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(SKILL_TYPE_COPY) as SkillType[]).map((skillType) => (
              <SelectItem key={skillType} value={skillType}>
                {SKILL_TYPE_COPY[skillType].label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">{SKILL_TYPE_COPY[shape.skillType].blurb}</p>
      </div>

      {shape.skillType === "POLICY" && (
        <div className="space-y-1.5">
          <Label>When it applies</Label>
          <Select
            value={shape.skillScope}
            onValueChange={(nextScope) => onChange({ skillScope: nextScope as SkillScope })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(SKILL_SCOPE_COPY) as SkillScope[]).map((skillScope) => (
                <SelectItem key={skillScope} value={skillScope}>
                  {SKILL_SCOPE_COPY[skillScope].label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">{SKILL_SCOPE_COPY[shape.skillScope].blurb}</p>
        </div>
      )}

      {shape.skillType === "PHASE" && (
        <>
          <div className="space-y-1.5">
            <Label>Tools for this stage</Label>
            <div className="flex flex-wrap gap-1.5">
              {AVAILABLE_AI_TOOLS.map((tool) => {
                const isSelected = shape.memberTools.includes(tool.name);
                return (
                  <button
                    key={tool.name}
                    type="button"
                    onClick={() => toggleTool(tool.name)}
                    className={`rounded-md border px-2 py-1 text-xs transition-colors ${
                      isSelected ? "border-primary bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50"
                    }`}
                  >
                    {tool.name}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground">
              The only tools available during this stage. The agent's own switches still apply: a
              tool turned off on the agent stays off here.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>When to hand off *</Label>
            <Textarea
              rows={3}
              value={shape.handoffDescription}
              onChange={(event) => onChange({ handoffDescription: event.target.value })}
              placeholder="e.g. the caller has agreed to a callback and a day and time need to be arranged"
            />
            <p className="text-xs text-muted-foreground">
              Read by the model, not by people. Be concrete about the moment to switch, because this
              is the only thing telling the agent when this stage begins.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>Opening question</Label>
            <Input
              value={shape.openingLine}
              onChange={(event) => onChange({ openingLine: event.target.value })}
              placeholder="e.g. What day and time would suit you for the callback?"
            />
            <p className="text-xs text-muted-foreground">
              Spoken to the caller if this stage doesn't start talking within a couple of seconds of
              taking over. Make it the question this stage needs answered anyway, so the pause moves
              the call forward instead of filling it.
            </p>
          </div>
        </>
      )}
    </>
  );
}
